import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, FolderOpen, ChevronDown, PlayCircle } from "lucide-react";

// Header entry point for accounts. Anonymous -> "Sign in" button; signed-in ->
// a menu with "My Work" and "Sign out". onOpenMyWork() switches the app to the
// library view. onAuthSuccess(user) lets the host claim the current session.
export default function AccountMenu({ onOpenMyWork, onAuthSuccess }) {
  const { user, isAuthed, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  if (user === undefined) return null; // still checking

  if (!isAuthed) {
    return (
      <>
        <button
          onClick={() => setAuthOpen(true)}
          data-testid="header-signin-button"
          className="inline-flex items-center gap-1.5 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-500 hover:text-[#8C3A2A] transition-colors"
        >
          <User className="h-3.5 w-3.5" /> Sign in
        </button>
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} onSuccess={onAuthSuccess} />
      </>
    );
  }

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="header-account-button"
          className="inline-flex items-center gap-2 text-stone-600 hover:text-[#8C3A2A] transition-colors"
        >
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#8C3A2A] text-white text-[12px] font-semibold">
            {initial}
          </span>
          <span className="hidden sm:inline text-[13px] max-w-[120px] truncate">{user.name || user.email}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="header-account-menu">
        <div className="px-2 py-1.5 text-[12px] text-stone-500 truncate">{user.email}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-my-work" onClick={onOpenMyWork} className="cursor-pointer">
          <FolderOpen className="h-4 w-4 mr-2" /> My Work
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-how-it-works" onClick={() => window.open("/how-it-works", "_blank", "noopener")} className="cursor-pointer">
          <PlayCircle className="h-4 w-4 mr-2" /> How it works
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-signout" onClick={logout} className="cursor-pointer text-red-600 focus:text-red-600">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
